import React, { useState } from 'react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from '../contexts/AuthContext';

interface GoogleJwtPayload {
  email: string;
  name: string;
  picture: string;
  sub: string;
}

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { loginWithGoogle, loading, error } = useAuth();
  const [localError, setLocalError] = useState<string | null>(null);

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    try {
      setLocalError(null);
      
      if (!credentialResponse.credential) {
        setLocalError('Erreur lors de la connexion Google');
        return;
      }

      // Décoder le JWT pour obtenir les infos de l'utilisateur
      const decoded = jwtDecode<GoogleJwtPayload>(credentialResponse.credential);
      console.log('Google user info:', decoded);

      // Vérifier si l'email est autorisé dans Baserow
      const success = await loginWithGoogle(decoded.email, decoded.name);
      
      if (success) {
        console.log('Connexion réussie avec Google');
        // Rediriger vers le dashboard
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      console.error('Erreur lors du traitement de la connexion Google:', err);
      setLocalError('Erreur lors de la connexion');
    }
  };

  const handleGoogleError = () => {
    setLocalError('Erreur lors de la connexion avec Google');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8 text-center">
            <h1 className="text-3xl font-bold text-white mb-2">
              Dashboard Formalités
            </h1>
            <p className="text-blue-100">Gestion de vos formalités</p>
          </div>

          {/* Content */}
          <div className="px-6 py-8">
            {/* Error Message */}
            {(error || localError) && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">{error || localError}</p>
              </div>
            )}

            {/* Instructions */}
            <div className="mb-6 text-center">
              <p className="text-gray-600 text-sm">
                Connectez-vous avec votre compte Google autorisé
              </p>
            </div>

            {/* Google Login Button */}
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap
                locale="fr"
              />
            </div>

            {loading && (
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600">Vérification en cours...</p>
              </div>
            )}

            {/* Footer */}
            <div className="mt-6 pt-6 border-t border-gray-200 text-center text-sm text-gray-600">
              <p>Authentification sécurisée par Google</p>
              <p className="mt-2 text-xs">
                Seuls les emails autorisés peuvent se connecter
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
